
import { useMemo, useState } from "react";
import {
  useAuditLogStore, useNoteStore, useContactStore,
  useDealStore, useDealTypeStore, useProjectStore,
  useTaskStore, useUserStore, useSyncStore,
} from "@/stores/StoreProvider";
import type { TimelineEntry } from "@/types/crm";
import type { AuditLog, Note } from "@/stores/types";

export const TIMELINE_KEY = "timeline";

export function resolveEntityName(
  entityType: string,
  entityId: string,
  stores: {
    contacts: ReturnType<typeof useContactStore>;
    deals: ReturnType<typeof useDealStore>;
    dealTypes: ReturnType<typeof useDealTypeStore>;
    projects: ReturnType<typeof useProjectStore>;
    tasks: ReturnType<typeof useTaskStore>;
  },
): string | undefined {
  switch (entityType) {
    case "contact": return stores.contacts.getById(entityId)?.name;
    case "deal": {
      const deal = stores.deals.getById(entityId);
      if (!deal) return undefined;
      return deal.dealTypeId ? stores.dealTypes.getById(deal.dealTypeId)?.name : undefined;
    }
    case "project": return stores.projects.getById(entityId)?.name;
    case "task": return stores.tasks.getById(entityId)?.title;
    case "dealtype": return stores.dealTypes.getById(entityId)?.name;
    default: return undefined;
  }
}

export function mapAuditToTimeline(
  audit: AuditLog,
  stores: Parameters<typeof resolveEntityName>[2],
  users: ReturnType<typeof useUserStore>,
): TimelineEntry {
  return {
    id: audit.id,
    type: "change",
    action: audit.action as TimelineEntry["action"],
    entityType: audit.entityType as TimelineEntry["entityType"],
    entityId: audit.entityId,
    entityName: resolveEntityName(audit.entityType, audit.entityId, stores),
    field: audit.field ?? undefined,
    oldValue: audit.oldValue ?? undefined,
    newValue: audit.newValue ?? undefined,
    userId: audit.userId ?? undefined,
    userName: audit.userId
      ? users.getById(audit.userId)?.displayName ?? audit.userName ?? undefined
      : audit.userName ?? undefined,
    timestamp: audit.createdAt,
    source: audit.source,
    toolName: audit.toolName,
  };
}

export function noteEntityTypeToString(entityType: string): TimelineEntry["entityType"] {
  switch (entityType) {
    case "contact": case "Contact": return "contact";
    case "deal": case "Deal": return "deal";
    case "task": case "Task": return "task";
    case "project": case "Project": return "project";
    default: return "contact";
  }
}

export function mapNoteToTimeline(
  note: Note,
  stores: Parameters<typeof resolveEntityName>[2],
  users: ReturnType<typeof useUserStore>,
): TimelineEntry {
  const entityType = noteEntityTypeToString(note.entityType);
  return {
    id: note.id,
    type: "note",
    action: "note",
    entityType,
    entityId: note.entityId,
    entityName: resolveEntityName(entityType, note.entityId, stores),
    noteContent: note.content,
    userId: note.userId ?? undefined,
    userName: note.userId
      ? users.getById(note.userId)?.displayName ?? undefined
      : undefined,
    timestamp: note.createdAt,
  };
}

export function getRelatedEntityIds(
  entityType: string,
  entityId: string,
  stores: Parameters<typeof resolveEntityName>[2],
): Set<string> {
  const ids = new Set<string>([entityId]);

  switch (entityType) {
    case "contact": {
      const deals = stores.deals.getByContactId(entityId);
      for (const d of deals) ids.add(d.id);
      const tasks = stores.tasks.getByContactId(entityId);
      for (const t of tasks) ids.add(t.id);
      for (const d of deals) {
        const dealTasks = stores.tasks.getByDealId(d.id);
        for (const t of dealTasks) ids.add(t.id);
      }
      break;
    }
    case "deal": {
      const tasks = stores.tasks.getByDealId(entityId);
      for (const t of tasks) ids.add(t.id);
      break;
    }
    case "project": {
      const deals = stores.deals.getByProjectId(entityId);
      for (const d of deals) ids.add(d.id);
      const tasks = stores.tasks.getByProjectId(entityId);
      for (const t of tasks) ids.add(t.id);
      for (const d of deals) {
        const dealTasks = stores.tasks.getByDealId(d.id);
        for (const t of dealTasks) ids.add(t.id);
      }
      break;
    }
  }

  return ids;
}

function useEntityStores() {
  const contacts = useContactStore();
  const deals = useDealStore();
  const dealTypes = useDealTypeStore();
  const projects = useProjectStore();
  const tasks = useTaskStore();
  return useMemo(() => ({ contacts, deals, dealTypes, projects, tasks }), [contacts, deals, dealTypes, projects, tasks]);
}

export function useGlobalTimeline(take = 20) {
  const auditLogStore = useAuditLogStore();
  const noteStore = useNoteStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const stores = useEntityStores();
  const [visibleCount, setVisibleCount] = useState(take);

  const allAudits = auditLogStore.all;
  const allNotes = noteStore.all;

  // Notes are read 100% from local MobX/IDB. Attachment SAS URLs are fetched
  // JIT per-note by <NoteAttachmentGallery> only when that component mounts.
  const entries = useMemo(() => {
    const auditEntries = allAudits.map(a => mapAuditToTimeline(a, stores, userStore));
    const noteEntries = allNotes.map(n => mapNoteToTimeline(n, stores, userStore));
    return [...auditEntries, ...noteEntries]
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [allAudits, allNotes, stores, userStore]);

  const visibleEntries = entries.slice(0, visibleCount);
  const hasMore = visibleCount < entries.length;

  return {
    entries: visibleEntries,
    isLoading: !syncStore.bootstrapComplete,
    hasMore,
    loadMore: () => setVisibleCount(c => c + take),
    isLoadingMore: false,
  };
}

export function useEntityTimeline(entityType: string, entityId: string, take = 20) {
  const auditLogStore = useAuditLogStore();
  const noteStore = useNoteStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const stores = useEntityStores();
  const [visibleCount, setVisibleCount] = useState(take);

  // Subscribe to store changes (length changes trigger recompute)
  const auditCount = auditLogStore.all.length;
  const noteCount = noteStore.all.length;

  const entries = useMemo(() => {
    if (!entityId) return [];
    const relatedIds = getRelatedEntityIds(entityType, entityId, stores);

    const auditEntries = auditLogStore.getByEntityIds(relatedIds)
      .map(a => mapAuditToTimeline(a, stores, userStore));

    // Attachment SAS URLs are fetched JIT by <NoteAttachmentGallery> on mount.
    const noteEntries = noteStore.getByEntityIds(relatedIds)
      .map(n => mapNoteToTimeline(n, stores, userStore));

    return [...auditEntries, ...noteEntries]
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditCount, noteCount, entityType, entityId, stores, userStore]);

  const visibleEntries = entries.slice(0, visibleCount);
  const hasMore = visibleCount < entries.length;

  return {
    entries: visibleEntries,
    isLoading: !syncStore.bootstrapComplete,
    hasMore,
    loadMore: () => setVisibleCount(c => c + take),
    isLoadingMore: false,
  };
}
